import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Location } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../components/dashboard/ConfirmDialog';
import { StatCard } from '../components/dashboard/StatCard';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { QueryEmptyState } from '../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { useDesktopLayout } from '../layout/useDesktopLayout';
import { api } from '../lib/api';
import { formatCount, formatPercent } from '../lib/dashboard/formatCount';
import { deriveScreenConnectionCounts } from '../lib/dashboard/screenStatus';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { nextLocationCopyName } from '../lib/location/nextLocationCopyName';
import { useOrganisationId } from '../lib/session';
import { AddDisplayModal } from './displays/AddDisplayModal';
import { AddLocationModal } from './displays/AddLocationModal';
import { DisplaysBoard } from './displays/DisplaysBoard';
import { DisplaysMobileHealth } from './displays/DisplaysMobileHealth';
import { DisplaysSetupHint } from './displays/DisplaysSetupHint';
import { EditLocationModal } from './displays/EditLocationModal';
import { DisplayManageDesktopSheet } from './displays/manage/DisplayManageDesktopSheet';
import { displayManagePath } from './displays/manage/displayManageRoutes';
import { useOpenDisplayManage } from './displays/manage/useOpenDisplayManage';

export function ScreensPage() {
  const { t } = useTranslation('displays');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useDesktopLayout();
  const [searchParams, setSearchParams] = useSearchParams();
  const [managingId, setManagingId] = useState<string | null>(null);
  const [managePlayContent, setManagePlayContent] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addLocationId, setAddLocationId] = useState<string | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deletingLocation, setDeletingLocation] = useState<Location | null>(null);

  useEffect(() => {
    if (location.hash === '#setup') {
      setAddLocationId(null);
      setAddOpen(true);
      setLocationOpen(false);
    }
    if (location.hash === '#location') {
      setLocationOpen(true);
      setAddOpen(false);
    }
  }, [location.hash]);

  const clearHash = useCallback(() => {
    if (location.hash === '#setup' || location.hash === '#location') {
      navigate({ pathname: '/screens', search: location.search }, { replace: true });
    }
  }, [location.hash, location.search, navigate]);

  const closeManage = useCallback(() => {
    setManagingId(null);
    setManagePlayContent(false);
    if (searchParams.has('manage') || searchParams.has('play')) {
      const next = new URLSearchParams(searchParams);
      next.delete('manage');
      next.delete('play');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openDesktopManage = useCallback((screenId: string, options?: { playContent?: boolean }) => {
    setManagePlayContent(Boolean(options?.playContent));
    setManagingId(screenId);
  }, []);

  const openManage = useOpenDisplayManage({ onDesktopOpen: openDesktopManage });

  useEffect(() => {
    const manageId = searchParams.get('manage');
    if (!manageId) return;

    if (!isDesktop) {
      navigate(
        displayManagePath(manageId, { playContent: searchParams.get('play') === '1' }),
        { replace: true },
      );
      return;
    }

    setManagePlayContent(searchParams.get('play') === '1');
    setManagingId(manageId);
  }, [searchParams, isDesktop, navigate]);

  useEffect(() => {
    if (!managingId) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [managingId]);

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    setAddLocationId(null);
    clearHash();
  }, [clearHash]);

  const closeLocation = useCallback(() => {
    setLocationOpen(false);
    clearHash();
  }, [clearHash]);

  const openAdd = useCallback((locationId?: string) => {
    setAddLocationId(locationId ?? null);
    setAddOpen(true);
  }, []);

  const openLocation = useCallback(() => {
    setLocationOpen(true);
  }, []);

  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    refetchInterval: 15_000,
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });

  useEffect(() => {
    if (!managingId || screensQuery.isPending || !screensQuery.data) return;
    if (!screensQuery.data.some((screen) => screen.id === managingId)) {
      closeManage();
    }
  }, [managingId, screensQuery.data, screensQuery.isPending, closeManage]);

  const { isRetrying, retry } = useQueryRetry(() => screensQuery.refetch());

  const invalidate = (key: string) =>
    queryClient.invalidateQueries({ queryKey: [key, organisationId] });

  const duplicateLocation = useMutation({
    mutationFn: (source: Location) => {
      const names = (locationsQuery.data ?? []).map((item) => item.name);
      return api.createLocation(organisationId, {
        name: nextLocationCopyName(source.name, names),
        timezone: source.timezone,
        ...(source.address ? { address: source.address } : {}),
      });
    },
    onSuccess: () => {
      void invalidate('locations');
    },
  });

  const deleteLocation = useMutation({
    mutationFn: (target: Location) => api.deleteLocation(organisationId, target.id),
    onSuccess: () => {
      void invalidate('locations');
      void invalidate('screens');
      setDeletingLocation(null);
    },
  });

  const screenHealth = screensQuery.data
    ? deriveScreenConnectionCounts(screensQuery.data)
    : null;
  const managingScreen = screensQuery.data?.find((s) => s.id === managingId);
  const pairedOnlineShare =
    screenHealth && screenHealth.total - screenHealth.unpaired > 0
      ? formatPercent(screenHealth.online, screenHealth.total - screenHealth.unpaired)
      : null;

  const locations = locationsQuery.data ?? [];
  const screens = screensQuery.data ?? [];
  const hasLocations = locations.length > 0;
  const hasScreens = screens.length > 0;
  const showFleetStats = hasScreens;

  const deletingDisplayCount = deletingLocation
    ? screens.filter((screen) => screen.locationId === deletingLocation.id).length
    : 0;

  return (
    <div className="space-y-6">
      {showFleetStats && screenHealth ? (
        <>
          <DisplaysMobileHealth counts={screenHealth} locationCount={locations.length} />
          <div className="hidden lg:block">
            <StatsGrid columns={5}>
              <StatCard
                label={t('screens.stats.totalDisplays')}
                value={formatCount(screens.length, screensQuery.isPending)}
                note={t('counts.locations', { count: locations.length })}
              />
              <StatCard
                label={t('screens.stats.online')}
                value={formatCount(screenHealth.online, screensQuery.isPending)}
                note={
                  pairedOnlineShare
                    ? t('screens.stats.onlineShare', { percent: pairedOnlineShare })
                    : undefined
                }
                tone="ok"
              />
              <StatCard
                label={t('screens.stats.offline')}
                value={formatCount(screenHealth.offline, screensQuery.isPending)}
                note={screenHealth.offline > 0 ? t('screens.stats.needsAttention') : undefined}
                tone="bad"
              />
              <StatCard
                label={t('screens.stats.unknown')}
                value={formatCount(screenHealth.unknown, screensQuery.isPending)}
                note={screenHealth.unknown > 0 ? t('screens.stats.noHeartbeat') : undefined}
                tone="warn"
              />
              <StatCard
                label={t('screens.stats.unpaired')}
                value={formatCount(screenHealth.unpaired, screensQuery.isPending)}
                note={t('screens.stats.noDevice')}
              />
            </StatsGrid>
          </div>
        </>
      ) : null}

      {screensQuery.isError || isRetrying ? (
        <QueryErrorState
          message={t('screens.loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : screensQuery.isPending || locationsQuery.isPending ? (
        <QueryLoadingBlock variant="cards" />
      ) : !hasLocations ? (
        <div className="flex flex-col items-start gap-3">
          <QueryEmptyState>{t('screens.emptyLocations')}</QueryEmptyState>
          <button
            type="button"
            onClick={openLocation}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--gs-accent-ink)]"
            style={{ background: 'var(--gs-accent-solid)' }}
          >
            {t('screens.addLocation')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {!hasScreens ? <DisplaysSetupHint /> : null}
          <DisplaysBoard
            screens={screens}
            locations={locations}
            onManageScreen={(screenId) => openManage(screenId)}
            onPlayContent={(screenId) => openManage(screenId)}
            onAddDisplay={openAdd}
            onEditLocation={setEditingLocation}
            onDuplicateLocation={(target) => duplicateLocation.mutate(target)}
            onDeleteLocation={setDeletingLocation}
            duplicatingLocationId={
              duplicateLocation.isPending && duplicateLocation.variables
                ? duplicateLocation.variables.id
                : null
            }
          />
        </div>
      )}

      {isDesktop && managingScreen ? (
        <DisplayManageDesktopSheet
          organisationId={organisationId}
          screen={managingScreen}
          playlists={playlistsQuery.data ?? []}
          startPlayContent={managePlayContent}
          onClose={closeManage}
        />
      ) : null}

      {addOpen ? (
        <AddDisplayModal
          organisationId={organisationId}
          locations={locations}
          preferredLocationId={addLocationId}
          onClose={closeAdd}
          onLocationCreated={() => invalidate('locations')}
          onScreenCreated={(screenId) => {
            void invalidate('screens');
            closeAdd();
            openManage(screenId);
          }}
        />
      ) : null}

      {locationOpen ? (
        <AddLocationModal
          organisationId={organisationId}
          onClose={closeLocation}
          onCreated={() => {
            void invalidate('locations');
          }}
        />
      ) : null}

      {editingLocation ? (
        <EditLocationModal
          organisationId={organisationId}
          location={editingLocation}
          onClose={() => setEditingLocation(null)}
          onSaved={() => {
            void invalidate('locations');
          }}
        />
      ) : null}

      {deletingLocation ? (
        <ConfirmDialog
          title={t('location.delete.title')}
          description={
            deletingDisplayCount > 0
              ? t('location.delete.descriptionWithDisplays', {
                  name: deletingLocation.name,
                  count: deletingDisplayCount,
                })
              : t('location.delete.description', { name: deletingLocation.name })
          }
          confirmLabel={t('location.delete.confirm')}
          tone="danger"
          confirming={deleteLocation.isPending}
          onCancel={() => {
            if (!deleteLocation.isPending) setDeletingLocation(null);
          }}
          onConfirm={() => deleteLocation.mutate(deletingLocation)}
        />
      ) : null}
    </div>
  );
}
